import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '~/components';
import { CopyButton } from '~/components/CopyButton/CopyButton';
import { IconButton } from '~/components/IconButton';
import type { ArtistContact } from '~/modules/artist-roster/lib/types';

interface ContactCardProps {
  contact: ArtistContact;
}

export function ContactCard({ contact }: ContactCardProps) {
  const { t } = useTranslation('roster');

  const handleVCardDownload = useCallback(() => {
    const vCard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${contact.firstName} ${contact.lastName}`,
      `N:${contact.lastName};${contact.firstName};;;`,
      contact.email ? `EMAIL:${contact.email}` : '',
      contact.phone ? `TEL:${contact.phone}` : '',
      'END:VCARD',
    ].join('\n');

    const blob = new Blob([vCard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${contact.title}_${contact.firstName}_${contact.lastName}.vcf`;
    link.click();

    URL.revokeObjectURL(url);
  }, [contact]);

  return (
    <div className="flex flex-col items-start gap-lg rounded-sm p-md bg-level-04">
      <div className="flex w-full items-center justify-between">
        <div className="flex min-w-0 flex-col">
          <span className="text-body-xs font-bold text-white">
            {contact.title}
          </span>
          <span className="truncate text-body-md">
            {t('contactInfo.fullName', {
              firstName: contact.firstName,
              lastName: contact.lastName,
            })}
          </span>
        </div>
        <div className="flex">
          <Tooltip
            content={t('contactInfo.downloadVCard')}
            delayDuration={100}
            sideOffset={4}
            side="top"
            size="sm"
            triggerAsChild
          >
            <IconButton
              iconType="arrow-down-to-line"
              iconVariant="light"
              className="ml-sm text-hover"
              data-testid="download-icon"
              onClick={handleVCardDownload}
            />
          </Tooltip>
        </div>
      </div>
      <div className="flex w-full flex-col text-body-sm">
        {contact.phone ? (
          <div className="group flex items-center justify-between">
            <div className="truncate">
              <a
                href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
                className="underline-offset-2 group-hover:underline"
              >
                {contact.phone}
              </a>
            </div>
            <CopyButton
              textToCopy={contact.phone}
              tooltipContent={t('contactInfo.copyNumber')}
              tooltipContentCopied={t('contactInfo.copyNumberCopied')}
              className="text-body-md opacity-none focus:opacity-full group-hover:opacity-full"
            />
          </div>
        ) : null}
        {contact.email ? (
          <div className="group flex h-[24px] items-center justify-between gap-sm">
            <div className="truncate">
              <a
                href={`mailto:${contact.email}`}
                className="underline-offset-2 group-hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                {contact.email}
              </a>
            </div>
            <CopyButton
              textToCopy={contact.email}
              tooltipContent={t('contactInfo.copyEmail')}
              tooltipContentCopied={t('contactInfo.copyEmailCopied')}
              className="hidden text-body-md focus:flex group-hover:flex"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
ContactCard.displayName = 'ContactCard';
